<script>
  import { onMount, setContext } from 'svelte';

  import { InlineNotification, TabContent } from 'carbon-components-svelte';

  import DangerIcon from 'carbon-icons-svelte/lib/WarningAltFilled16';

  import AuditTable from '../../components/audit-table/AuditTable.svelte';
  import LazyLoadedTabContent from '../../components/LazyLoadedTabContent.svelte';
  import GroupBasicInformation from './GroupBasicInformation.svelte';
  import GroupExternalKeyList from './GroupExternalKeyList.svelte';
  import GroupKeyList from './GroupKeyList.svelte';
  import GroupLicenseList from './GroupLicenseList.svelte';
  import GroupPermissionsRouter from './group-permissions/GroupPermissionsRouter.svelte';
  import GroupCloudPrincipalsList from './GroupCloudPrincipalsList.svelte';
  import GroupTargetList from './GroupTargetList.svelte';
  import GroupUserList from './GroupUserList.svelte';
  import LoadingButton from '../../components/LoadingButton.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PoliciesModal from './PoliciesModal.svelte';
  import TabsWithRouting from '../../components/TabsWithRouting.svelte';

  import { getGroup, getPolicies } from '../../services';
  import { cacheEntityName } from '../../stores';

  const DEBOUNCE_MS = 1000;

  export let groupId;

  let pageData = { loading: true };
  let policyButtonState = {
      policies: [],
      mostRecentReconciliationData: null,
      error: null,
      loading: false,
    },
    debounce;

  let modals = {
    policies: { open: false },
  };

  const loadGroupData = async () => {
    pageData = { ...pageData, loading: true };
    try {
      const group = await getGroup(groupId);
      fetchPolicies();
      pageData = { loading: false, group };
      cacheEntityName(groupId, group.name);
    } catch (error) {
      console.error('[GroupDetails] Failed to retrieve group data!', error);
      pageData = { loading: false, error };
    }
  };

  const fetchPolicies = async () => {
    policyButtonState = { ...policyButtonState, loading: true, error: null };
    try {
      const results = await getPolicies(groupId);
      policyButtonState = { ...policyButtonState, ...results, loading: false };
    } catch (error) {
      console.error('[GroupDetails] Failed to retrieve group policies!', error);
      policyButtonState = { ...policyButtonState, error, loading: false };
    }
  };

  function debounceFetchPolicies() {
    clearTimeout(debounce);

    policyButtonState = { ...policyButtonState, loading: true, error: null };

    debounce = setTimeout(async () => {
      fetchPolicies();
    }, DEBOUNCE_MS);
  }

  function closeModal(name) {
    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  function openModal(name, forwardData) {
    modals = {
      ...modals,
      [name]: { open: true, ...forwardData },
    };
  }

  onMount(loadGroupData);

  $: setContext('group', pageData.group);
  $: hasModifiedPolicies = policyButtonState.policies.length || policyButtonState.mostRecentReconciliationData?.error;
  $: policiesInDanger = policyButtonState.mostRecentReconciliationData?.error;
  $: policiesCloseToLimit = policyButtonState.policies.length > 16;
</script>

{#if pageData.loading}
  <LoadingSpinner withOverlay={false} />
{:else if pageData.error}
  <InlineNotification
    hideCloseButton
    kind="error"
    lowContrast
    title="Error:"
    subtitle="Failed to load group data! Please try again later."
  />
{/if}

{#if !pageData.loading || pageData.group}
  <div class="group-tabs">
    <TabsWithRouting
      defaultHash="#basic-information"
      tabsWrapperProps={{ 'aria-label': 'Group details tabs' }}
      tabs={[
        { label: 'Basic Information', hash: '#basic-information' },
        { label: 'Users', hash: '#users' },
        { label: 'Cloud Principals', hash: '#cloud-principals' },
        { label: 'SSH Keys', hash: '#ssh-keys' },
        { label: 'External Services Keys', hash: '#external-keys' },
        { label: 'Build Targets', hash: '#parameters' },
        { label: 'Licenses', hash: '#licenses' },
        { label: 'Permissions', hash: '#permissions', activeForChildHashes: true },
        { label: 'History', hash: '#history' },
      ]}
    >
      <svelte:fragment slot="banner">
        {#if hasModifiedPolicies}
          <div class="banner" class:danger={policiesInDanger} class:warning={!policiesInDanger && policiesCloseToLimit}>
            <LoadingButton
              isLoading={policyButtonState.loading}
              kind="secondary"
              size="small"
              icon={policiesInDanger ? DangerIcon : null}
              on:click={() => openModal('policies')}
            >
              {#if policyButtonState.policies.length}
                {policyButtonState.policies.length}/20 IAM policies in use
              {:else}
                Error syncing policies
              {/if}
            </LoadingButton>
          </div>
        {/if}
      </svelte:fragment>
      <TabContent><GroupBasicInformation {groupId} group={pageData.group} {loadGroupData} /></TabContent>
      <LazyLoadedTabContent hash="#users"
        ><GroupUserList
          {groupId}
          atfUsername={pageData.group?.atfUsername}
          organizationId={pageData.group?.organizationId}
        /></LazyLoadedTabContent
      >
      <LazyLoadedTabContent hash="#cloud-principals"><GroupCloudPrincipalsList {groupId} /></LazyLoadedTabContent>
      <LazyLoadedTabContent hash="#ssh-keys"><GroupKeyList group={pageData.group} /></LazyLoadedTabContent>
      <LazyLoadedTabContent hash="#external-keys">
        <GroupExternalKeyList {groupId} groupName={pageData.group?.name} />
      </LazyLoadedTabContent>
      <LazyLoadedTabContent hash="#parameters"><GroupTargetList groupName={pageData.group?.name} {groupId} /></LazyLoadedTabContent>
      <LazyLoadedTabContent hash="#licenses">
        <GroupLicenseList
          {groupId}
          shortName={pageData.group?.shortName}
          licenseLookbackDays={pageData.group?.licenseLookbackDays}
          groupName={pageData.group?.name}
          sftp05Username={pageData.group.sftp05Username}
          atfUsername={pageData.group.atfUsername}
        />
      </LazyLoadedTabContent>
      <TabContent><GroupPermissionsRouter {groupId} groupName={pageData.group?.name} /></TabContent>
      <LazyLoadedTabContent hash="#history"><AuditTable entityId={groupId} entityType="group" /></LazyLoadedTabContent>
    </TabsWithRouting>
  </div>
{/if}

{#if modals.policies.open}
  <PoliciesModal open group={pageData.group} state={policyButtonState} on:close={() => closeModal('policies')} {debounceFetchPolicies} />
{/if}

<style>
  .group-tabs :global(.bx--tab-content) {
    padding-top: 1.5rem;
  }

  .banner {
    display: flex;
    flex-direction: column;
    position: absolute;
    top: 0;
    width: 100%;
    transform-origin: top;
    animation: expand-from-top 300ms ease-out;
    opacity: 0.85;
  }

  @keyframes expand-from-top {
    from {
      transform: scaleY(0);
      opacity: 0;
    }
    to {
      transform: scaleY(1);
      opacity: 0.85;
    }
  }

  .banner.danger :global(.bx--btn) {
    background-color: var(--cds-hover-danger);
  }

  .banner.warning :global(.bx--btn--secondary) {
    background-color: var(--outline-warning-color);
  }

  .banner.warning :global(.bx--btn--secondary):hover {
    background-color: #a86d00;
  }

  .banner.warning :global(.bx--btn--secondary):active {
    background-color: #704901;
  }

  .banner :global(.bx--btn) {
    font-family: 'Trebuchet MS', sans-serif;
    font-size: 0.65rem;
    padding: 0 10px !important;
    max-width: unset;
    display: flex;
    justify-content: center;
    min-height: 1rem;
    background-color: var(--cds-active-secondary);
  }

  .banner :global(svg),
  .banner :global(.bx--loading--small) {
    width: 0.75rem;
    height: 0.75rem;
  }

  @media (min-width: 42rem) {
    .group-tabs :global(.bx--tabs__nav-item:nth-child(5) .bx--tabs__nav-link) {
      width: 13rem;
    }
  }
</style>
